import React, { useEffect, useState } from "react";
import { Crosshair, ScanEye, Settings } from "lucide-react";
import { InspectorVisibilityEyeButton } from "../components/ui/InspectorVisibilityEyeButton";
import { InspectorVisibilitySection } from "../components/property-panels/InspectorVisibilitySection";
import VisibilityIconRow from "../components/property-panels/VisibilityIconRow";
import FieldLabel from "../components/ui/FieldLabel";
import Tooltip from "../components/ui/Tooltip";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../utils/inspectorChromeButtonClasses";
import { useSiteVisibilityControls } from "./useSiteVisibilityControls";
import {
  normalizeCollisionGeomHexColor,
  useViewerActions,
  useViewerStore,
} from "../stores/viewerStore";

const SiteVisibilityToolbar = ({ siteUuid, className = "", sectionTitle = "Site Visibility" }) => {
  const siteVisibility = useViewerStore((s) => s.siteVisibility);
  const siteVizColor = useViewerStore((s) => s.siteVizColor);
  const thisSiteVisible = siteUuid ? (siteVisibility[siteUuid] ?? true) : true;
  const siteVizSelectedColor = useViewerStore((s) => s.siteVizSelectedColor);
  const viewerActions = useViewerActions();
  const [colorPanelOpen, setColorPanelOpen] = useState(false);
  const [siteHexDraft, setSiteHexDraft] = useState(siteVizColor);
  const [siteSelectedHexDraft, setSiteSelectedHexDraft] = useState(siteVizSelectedColor);

  useEffect(() => {
    if (colorPanelOpen) {
      setSiteHexDraft(siteVizColor);
      setSiteSelectedHexDraft(siteVizSelectedColor);
    }
  }, [colorPanelOpen, siteVizColor, siteVizSelectedColor]);

  const { state, disabled, focusDisabled, toggleFocus, toggleOrientationAxes } =
    useSiteVisibilityControls({ siteUuid, enabled: true });

  const buttons = [
    {
      key: "focus",
      title: "Display selected site only",
      on: state.focus,
      onClick: toggleFocus,
      disabled: focusDisabled,
      Icon: ScanEye,
    },
    {
      key: "orientation-axes",
      title: "Show orientation axes",
      on: state.orientationAxes,
      onClick: toggleOrientationAxes,
      disabled,
      Icon: Crosshair,
    },
  ];

  const handleSiteColorWellChange = (hex) => {
    viewerActions.setSiteVizColor(hex);
    setSiteHexDraft(hex);
  };

  const handleSiteHexTextChange = (raw) => {
    setSiteHexDraft(raw);
    const normalized = normalizeCollisionGeomHexColor(raw);
    if (normalized) {
      viewerActions.setSiteVizColor(normalized);
    }
  };

  const handleSiteSelectedWellChange = (hex) => {
    viewerActions.setSiteVizSelectedColor(hex);
    setSiteSelectedHexDraft(hex);
  };

  const handleSiteSelectedHexTextChange = (raw) => {
    setSiteSelectedHexDraft(raw);
    const normalized = normalizeCollisionGeomHexColor(raw);
    if (normalized) {
      viewerActions.setSiteVizSelectedColor(normalized);
    }
  };

  return (
    <InspectorVisibilitySection
      sectionTitle={sectionTitle}
      className={className}
      headerActions={
        <div className="flex items-center gap-1">
          <InspectorVisibilityEyeButton
            visible={thisSiteVisible}
            onToggle={() => siteUuid && viewerActions.setSiteVisibility(siteUuid, !thisSiteVisible)}
            tooltipWhenOn="This site is visible — click to hide"
            tooltipWhenOff="This site is hidden — click to show"
            ariaLabel="Toggle this site's visibility"
          />
          <Tooltip content={colorPanelOpen ? "Hide site colors" : "Site marker colors"}>
            <button
              type="button"
              aria-label="Site marker color options"
              aria-expanded={colorPanelOpen}
              className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
              style={{
                backgroundColor: "transparent",
                opacity: colorPanelOpen ? 1 : 0.55,
              }}
              onClick={() => setColorPanelOpen((open) => !open)}
            >
              <Settings className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      }
    >
      <>
        <VisibilityIconRow actions={buttons} />
        {colorPanelOpen ? (
          <div className="space-y-3 px-0.5">
            <div>
              <FieldLabel as="label" className="mb-1 block text-xs text-gray-400">
                Unselected sites
              </FieldLabel>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={siteVizColor}
                  onChange={(e) => handleSiteColorWellChange(e.target.value)}
                  className="h-8 w-12 cursor-pointer rounded border border-gray-600"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    value={siteHexDraft}
                    onChange={(e) => handleSiteHexTextChange(e.target.value)}
                    className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-gray-100"
                    placeholder="#ffff00"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </div>
            <div>
              <FieldLabel as="label" className="mb-1 block text-xs text-gray-400">
                Selected site
              </FieldLabel>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={siteVizSelectedColor}
                  onChange={(e) => handleSiteSelectedWellChange(e.target.value)}
                  className="h-8 w-12 cursor-pointer rounded border border-gray-600"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    value={siteSelectedHexDraft}
                    onChange={(e) => handleSiteSelectedHexTextChange(e.target.value)}
                    className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-gray-100"
                    placeholder="#ff00ff"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </>
    </InspectorVisibilitySection>
  );
};

export default React.memo(SiteVisibilityToolbar);
